<script lang="ts">
  import { onMount } from 'svelte';
  import { cubicIn } from 'svelte/easing';
  import { Canvas } from '@threlte/core';
  import { useProgress } from '@threlte/extras';
  import type { HomepageHardwareScrollV2Props, SceneParameters } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { lerp } from 'three/src/math/MathUtils.js';
  import WebGL from 'three/addons/capabilities/WebGL.js';
  import layoutHelpers from '$lib/helpers/layout-helpers';
  import { checkSrc } from '$lib/helpers/asset-verification-helpers';
  import PerformanceMonitor from '$components/common/partials/PerformanceMonitor.svelte';
  import Scene from './ThrelteScene.svelte';
  import Fallback from './Fallback.svelte';
  import { WebGLRenderer } from 'three';
  import { Text } from '@squareup/public-web-design-system';

  // #region Props
  type $$Props = HomepageHardwareScrollV2Props;

  export let entryId: HomepageHardwareScrollV2Props['entryId'];
  export let sectionContent: HomepageHardwareScrollV2Props['sectionContent'];
  export let bgImgLayouts: HomepageHardwareScrollV2Props['bgImgLayouts'];
  export let imgSequenceConfig: HomepageHardwareScrollV2Props['imgSequenceConfig'];
  export let scene: HomepageHardwareScrollV2Props['scene'];
  export let eyebrow: HomepageHardwareScrollV2Props['eyebrow'];
  export let headline: HomepageHardwareScrollV2Props['headline'];
  export let bankingDisclaimer: HomepageHardwareScrollV2Props['bankingDisclaimer'];
  export let fallbackImgUpgradeDelay: HomepageHardwareScrollV2Props['fallbackImgUpgradeDelay'];
  export let enableQueriesOnProd: HomepageHardwareScrollV2Props['enableQueriesOnProd'];
  export let skipHardwareIntro: HomepageHardwareScrollV2Props['skipHardwareIntro'];
  export let customCssClass: HomepageHardwareScrollV2Props['customCssClass'];

  // #endregion

  // #region Setup
  const params: SceneParameters = {
    cameraMouseSpeed: 0.1,
    scrollLerpSpeed: 0.1,
    transitionDuration: 500,
    imageDelay: 100,
    reduceMotion: false,
    t3LongAnim: true,
    t3AnimOffset: -0.2,
  };

  const {
    active, // Readable<boolean> – if the DefaultLoadingManager is active
  } = useProgress();

  let windowHeight = 2048;
  let w: number;
  let h: number;
  let y: number = 0;
  let smoothY = y;
  let progress = 0;
  const delayPercentage = 0;
  let threlteScene: HTMLElement;
  const totalSections = sectionContent.length;

  const SMALL_BREAKPOINT = layoutHelpers.BREAKPOINT_WIDTHS.small.minWidth;
  const MOBILE_BREAKPOINT = layoutHelpers.BREAKPOINT_WIDTHS.medium.minWidth;

  $: isMobile = w < MOBILE_BREAKPOINT;
  $: isSmall = w < SMALL_BREAKPOINT;
  $: isLandscape = w > h;
  $: videoSection = Math.min(Math.max(Math.floor((y - windowHeight) / windowHeight), 0), totalSections);

  $: section = isRender3d ? Math.min(threlteSection, totalSections) : Math.min(videoSection, totalSections);
  $: sceneTop = threlteScene ? threlteScene.offsetTop : windowHeight;
  $: threlteSection = Math.max(Math.floor((smoothY - sceneTop) / windowHeight), 0) + Number(skipHardwareIntro);
  $: activeSection = scene.sections[Math.max(section - 1, 0)];

  $: sectionProgress = (smoothY - sceneTop) / windowHeight - section + Number(skipHardwareIntro);
  $: totalProgress = section + sectionProgress;

  let lastSection = false;
  let hardwareContainer: HTMLElement;
  let isTabbing = false;
  const sectionElements: HTMLElement[] = [];

  $: lastSection = section >= totalSections;
  // #endregion

  // #region 3D & Performance
  const flipflops = 2;
  let pixelRatio: number;
  let pixelRatioDropoff: number;

  let triggerFallback = () => {};
  let isShowPerformanceMonitor = false;
  let isRender3d = !isSmall; // Disable 3D rendering if small mobile breakpoint, enable on desktop
  $: {
    if (!isRender3d) {
      triggerFallback();
    }
  }

  const validateSceneResources = () => {
    const resources = [
      { urls: scene.textures, type: 'textures' },
      { urls: scene.models, type: 'models' },
    ];
    const texturesValid = Object.values(resources[0].urls).every((model) =>
      Object.values(model).every((value: any) => checkSrc(value, 'textures'))
    );
    const modelsValid = Object.values(resources[1].urls).every((url) => checkSrc(url, 'models'));
    return texturesValid && modelsValid;
  };

  // used for generating fallback
  let isPreserveDrawingBuffer = false; // when true, images can be captured from canvas (has performance cost)
  $: rendererParameters = {
    preserveDrawingBuffer: isPreserveDrawingBuffer,
  };

  // check for webgl2 support
  isRender3d = WebGL.isWebGL2Available();
  // check model url validity
  isRender3d = isRender3d && validateSceneResources();
  // #endregion

  // #region Handler
  function handleResize() {
    windowHeight = document.querySelector('.hardware-sections')?.scrollHeight || windowHeight;
  }

  function handleDownscale() {
    if (!pixelRatioDropoff) {
      pixelRatioDropoff = (pixelRatio - 1) / flipflops;
    }
    pixelRatio -= pixelRatioDropoff;
  }

  function handleFallback() {
    isRender3d = false;
  }
  // #endregion

  // #region Lifecycle
  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return Object.fromEntries(urlParams.entries());
  };

  const updatePropsFromParams = () => {
    const hostname = window.location.hostname;

    // Regex pattern to match 'xms-fe-*-playpen-*--xms-fe.stage.sqprod.co'
    const stageRegex = /^xms-fe-[^-]+-playpen-[^-]+--xms-fe\.stage\.sqprod\.co$/;

    const allowedHosts = ['localhost', 'squareupstaging.com']; // only allow our queries to work in dev and staging
    if (enableQueriesOnProd) {
      allowedHosts.push('squareup.com');
    }

    // Check if the hostname matches any allowed hosts or the stage regex
    if (allowedHosts.includes(hostname) || stageRegex.test(hostname)) {
      const urlParams = getUrlParams();
      if (urlParams.isRender3d !== undefined) {
        isRender3d = urlParams.isRender3d === 'true';
      }
      if (urlParams.isShowPerformanceMonitor !== undefined) {
        isShowPerformanceMonitor = urlParams.isShowPerformanceMonitor === 'true';
      }

      if (urlParams.isPreserveDrawingBuffer !== undefined) {
        isPreserveDrawingBuffer = urlParams.isPreserveDrawingBuffer === 'true';
      }
    }
  };

  let scrollLoop: number;
  function smoothScroll() {
    smoothY = lerp(smoothY, y, params.scrollLerpSpeed);
    scrollLoop = requestAnimationFrame(smoothScroll);
    progress = Number(lerp(progress, percentageSeen(threlteScene), params.scrollLerpSpeed).toFixed(5));
  }

  // Calculates the "percentage seen" based on when the image first enters the screen until the moment it leaves
  function percentageSeen(element: HTMLElement) {
    const canvasPosY = element.getBoundingClientRect().top + smoothY;
    const distance = smoothY + windowHeight - canvasPosY;
    let percentage = distance / ((windowHeight + threlteScene.scrollHeight) / 100);
    percentage = Math.max(0, Math.min(100, percentage - delayPercentage));

    return percentage;
  }

  onMount(() => {
    params.reduceMotion = window.matchMedia('prefers-reduced-motion: reduce').matches;
    windowHeight = window.innerHeight;
    updatePropsFromParams();
    smoothScroll();
    addFocusListeners();
    percentageSeen(threlteScene);

    return () => {
      cancelAnimationFrame(scrollLoop);
      removeFocusListeners();
    };
  });
  // #endregion

  function showSection(event: FocusEvent) {
    if (!isTabbing) {
      return;
    }

    const target = event.currentTarget as HTMLElement;
    const sectionIndex = sectionElements.findIndex((el) => el.contains(target));
    const newSection = sectionIndex + 2;

    hardwareContainer.scrollTo({ top: 0, behavior: 'smooth' });

    // Adjust y based on whether it's mobile
    y = (newSection + 1) * windowHeight + (isMobile ? -1 : 1);
    smoothY = y;
  }

  function handleKeyDown(event: KeyboardEvent) {
    isTabbing = event.key === 'Tab';
  }

  function handleMouseDown() {
    isTabbing = false;
  }

  function addFocusListeners() {
    sectionElements.forEach((section) => {
      const anchor = section.querySelector('a') as HTMLAnchorElement;
      anchor.addEventListener('focus', showSection);
    });
  }

  function removeFocusListeners() {
    sectionElements.forEach((section) => {
      const anchor = section.querySelector('a') as HTMLAnchorElement;
      anchor.removeEventListener('focus', showSection);
    });
  }
</script>

<svelte:window on:mousedown={handleMouseDown} on:keydown={handleKeyDown} bind:scrollY={y} on:resize={handleResize} />
<PerformanceMonitor
  bind:triggerFallback
  isDisplayGui={isShowPerformanceMonitor}
  {flipflops}
  onDecline={handleDownscale}
  onFallback={handleFallback}
/>
<div
  class="HomepageHardwareScroll HomepageHardwareScroll-{entryId}-box-properties {customCssClass}"
  data-entry-id={entryId}
  style="--sections: {totalSections + Number(!skipHardwareIntro)}; --windowHeight: {windowHeight}px;"
>
  <div
    class="threlte"
    class:fallback={!isRender3d && !isSmall}
    class:is-mobile={isMobile}
    class:is-small={isSmall}
    bind:this={threlteScene}
  >
    <div class="threlte-container" bind:clientWidth={w} bind:clientHeight={h}>
      {#if section <= 0 && (eyebrow || headline) && !isSmall && (!skipHardwareIntro || (skipHardwareIntro && !isRender3d))}
        <div class="hardware-title" style={`opacity: ${cubicIn(Math.min(1, Math.max(0, 2 - totalProgress * 2)))}`}>
          {#if eyebrow}
            <Text as="h5" variant="100">{eyebrow}</Text>
          {/if}
          {#if headline}
            <Text as="h2" variant="1000">{headline}</Text>
          {/if}
        </div>
      {/if}
      <div class="hardware-sections">
        {#if !isRender3d && !isSmall}
          <Fallback
            {imgSequenceConfig}
            {fallbackImgUpgradeDelay}
            {windowHeight}
            {y}
            {isMobile}
            section={videoSection}
          />
        {/if}

        {#each sectionContent as content, index}
          <div
            class={`background-images layout-${bgImgLayouts[index]}`}
            class:is-visible={isSmall || section - 1 === index || (index === totalSections - 1 && lastSection)}
          >
            {#if !isSmall}
              {#each content.imageContainers as imageContainer}
                <div class="background-image">
                  <DynamicChildrenComponent children={[imageContainer]} />
                </div>
              {/each}
            {/if}
          </div>
        {/each}
        {#if isRender3d && !isSmall}
          <div class="threlte-scene">
            <div class="threlte-scene-loader" class:isLoading={$active} />
            <Canvas
              createRenderer={(canvas) =>
                new WebGLRenderer({
                  canvas,
                  alpha: true,
                  antialias: false,
                  ...rendererParameters,
                })}
            >
              <Scene
                {section}
                sectionPercentage={sectionProgress}
                {totalProgress}
                {isMobile}
                {isLandscape}
                {scene}
                {params}
                forceFallback={handleFallback}
                bind:pixelRatio
              />
            </Canvas>
          </div>
        {/if}
      </div>
      <div class="copy-section-container" bind:this={hardwareContainer}>
        {#each sectionContent as content, index}
          <div
            class="copy-section"
            class:is-visible={isSmall || section - 1 === index || (index === totalSections - 1 && lastSection)}
            bind:this={sectionElements[index]}
          >
            <div class="copy" class:fallback={!isRender3d}>
              <div class="headline">
                <DynamicChildrenComponent children={[content.headline]} />
                <DynamicChildrenComponent children={[content.link]} />
              </div>
            </div>
            {#if activeSection === 'banking' && bankingDisclaimer}
              <div class="disclaimer hide-until-medium">
                <DynamicChildrenComponent children={[bankingDisclaimer]} />
              </div>
            {/if}
            {#if isSmall}
              <div class="copy-section-images">
                {#each content.imageContainers as imageContainer}
                  <div class="background-image">
                    <DynamicChildrenComponent children={[imageContainer]} />
                  </div>
                {/each}
              </div>
              {#if content.mobileImage}
                <div class="foreground-image">
                  <DynamicChildrenComponent children={[content.mobileImage]} />
                </div>
              {/if}
            {/if}
          </div>
          {#if !isSmall}
            <div
              class="spacer"
              class:is-visible={section - 2 === index || (index === totalSections - 1 && lastSection)}
            />
          {/if}
        {/each}
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/_media_queries' as mediaQueries;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/variables.scss' as vars;
  :global {
    body {
      --navHeight: 69px;
      background: #ffffff;
    }
    .PublicWebNav nav.DesktopNav,
    .PublicWebNav nav.MobileNav {
      --nav-outer-gutter-base: clamp(16px, 5vw, 40px);
      background-color: #ffffff !important;
      border-color: transparent !important;
    }
    .PublicWebNav nav.MobileNav,
    .PublicWebNavV2 .GlobalNav .MobileNav,
    .PublicWebNavV2 .GlobalNav .DesktopNav {
      background-color: #ffffff !important;
      border-color: transparent !important;
    }
    .PublicWebNav .CtaSubnav .header,
    .PublicWebNavV2 .CtaSubnav .header {
      display: none;
      --color-background: #ffffff;
      box-shadow: none !important;
      border: none !important;
    }
    .PublicWebNav .CtaSubnavDesktopNav .left-slot,
    .PublicWebNavV2 .CtaSubnavDesktopNav .left-slot {
      align-content: center;
    }

    .PublicWebNavV2 .CtaSubnav .footer,
    .PublicWebNav .CtaSubnav .footer {
      --color-background: #ffffff;
      border: none;
      bottom: auto;
      box-shadow: none;
      transform: translateY(-100%);
      position: fixed;
      top: 0;
      display: flex;

      &.isVisible {
        transform: translateY(0) !important;
        @include mediaQueries.until('medium') {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
      }

      @include mediaQueries.until('large') {
        &.disableLeftCtaIcon {
          flex-direction: row;
        }
      }

      @include mediaQueries.at('large') {
        display: none;
      }
    }
  }

  @layer custom-components {
    :global(.BasePage .StickyBannerSection) {
      background-color: #ffffff;
      box-shadow: none;
      padding: var(--space-10) clamp(16px, 5vw, 40px);
    }

    :global(h2, h3) {
      font-weight: var(--text-font-weight-regular, var(--font-weight-regular));
    }

    :global(.sq-custom-disclaimer-link a) {
      color: #959595;
      font-weight: 400;
    }

    :global(.sq-custom-disclaimer-link p) {
      margin: 0 auto;
    }

    .copy {
      &-section {
        --navHeight: 69px;
        --sectionSpacing: 32px;

        display: grid;
        column-gap: var(--space-10);
        grid-template-columns: repeat(8, 1fr);
        height: 100svh;
        max-width: 1920px;
        left: 50%;
        opacity: 0;
        padding-bottom: var(--sectionSpacing);
        padding-left: var(--space-40);
        padding-top: var(--navHeight);
        top: 0;
        transform: translate(-50%, 30px);
        transition: transform 0.5s ease;
        width: 100%;
        z-index: 100;

        @media (min-width: vars.$breakpoint-width-small) {
          grid-template-columns: repeat(12, 1fr);
        }

        &.hide-at-medium {
          @media (min-width: vars.$breakpoint-width-small) {
            display: none;
          }
        }

        &.is-visible {
          opacity: 1;
          position: absolute;
          padding-left: 0;
          transform: translate(-50%, 0);
          transition:
            transform 0.5s ease,
            opacity 0.5s ease;
          top: 0;
          width: calc(100% - var(--space-20));
          @media (min-width: vars.$breakpoint-width-small) {
            width: calc(100% - var(--space-80));
          }

          .copy-section-container,
          .disclaimer {
            opacity: 1;
            transform: translateY(0);
            transition: all 0.4s cubic-bezier(0.42, 0, 0.58, 1);
          }

          .disclaimer {
            align-self: flex-end;
          }
        }

        @media (min-width: vars.$breakpoint-width-small) {
          justify-content: flex-start;
        }

        &-container {
          left: 0;
          position: absolute;
          width: 100%;
        }

        // Copy
        .copy {
          bottom: 20px;
          grid-column: 1;
          position: absolute;
          pointer-events: all;

          @media (prefers-reduced-motion) {
            transform: translateY(0px);
          }

          @media (min-width: calc(vars.$breakpoint-width-small)) {
            grid-column: 1 / -1;
            position: static;
          }

          &.fallback {
            //adjust fallback copy position for shorter screens
            @media screen and (max-height: 667px) and (orientation: portrait) {
              bottom: 20px;
            }
          }
        }

        .disclaimer {
          grid-column: 1 / -1;
          opacity: 0;
          transform: translateY(var(--space-40));

          @media (min-width: vars.$breakpoint-width-small) {
            grid-column: 10 / -1;
          }

          &.hide-until-medium {
            @media (max-width: vars.$breakpoint-width-small) {
              display: none;
            }
          }

          :global {
            p {
              font-size: inherit;
              text-align: left;
              text-wrap: balance;

              span {
                line-height: 0;
              }
            }
          }
        }

        // Headline
        .headline {
          max-width: 42rem;

          :global {
            h2,
            h3 {
              margin-bottom: 3rem;

              text-wrap: balance;
              font-size: clamp(2.4rem, 7vw, 4.8rem);
              font-weight: var(--text-font-weight-regular, var(--font-weight-regular));
              line-height: clamp(2.6rem, 7.25vw, 5.2rem);

              span {
                display: block;
              }
            }

            h3 span {
              font-size: clamp(2.4rem, 7vw, 4.8rem);
              font-weight: var(--text-font-weight-regular, var(--font-weight-regular));
            }
          }
        }

        // CTA
        :global {
          .LinkComponent {
            .arrow {
              span {
                &::after {
                  content: '→';
                  display: inline-flex;
                  align-items: center;
                  aspect-ratio: 1 / 1;
                  margin-left: 0.625em;
                  padding: 0.6rem 0.8rem;
                  background-color: var(--color-blue);
                  border-radius: 10rem;
                  color: white;
                  line-height: 1;
                  height: 26px;
                  width: 26px;
                  justify-content: center;

                  @media (min-width: vars.$breakpoint-width-medium) {
                    height: 30px;
                    width: 30px;
                  }
                }
              }
            }
          }
        }

        &-spacer {
          display: none;
          height: 120vh;
          left: 50%;
          padding-bottom: var(--sectionSpacing);
          padding-top: calc(var(--navHeight) + var(--sectionSpacing));
          transform: translateX(-50%);
          top: 0;
          width: calc(100% - var(--space-20));
          @media (min-width: vars.$breakpoint-width-small) {
            width: calc(100% - var(--space-80));
          }
          &.is-visible {
            display: block;
          }
        }
      }
    }

    .hardware-sections {
      display: flex;
      flex-direction: column;
      height: 100svh;
      left: 0;
      justify-content: center;
      overflow: clip;
      pointer-events: none;
      position: absolute;
      top: 0;
      width: 100%;
    }

    .hardware {
      &-title {
        margin: 0 auto;
        padding: var(--space-60) 0;
        text-align: center;
        width: var(--container-width-current);

        :global(h2) {
          font-family: var(--font-family, 'Square Sans Display');
          font-size: clamp(6.8rem, 9vw, 14rem) !important;
          line-height: clamp(6.6rem, 8vw, 14.5rem);
        }
        :global(h5) {
          font-family: var(--font-family, 'Square Sans Display');
          font-size: clamp(20px, 5vw, 40px);
          margin: 0 auto;
          padding-bottom: var(--space-40);
          text-align: center;
        }
      }
    }

    .background {
      &-images {
        --navHeight: 69px;
        --sectionSpacing: clamp(10px, 3vh, 75px);

        column-gap: var(--space-10);
        display: grid;
        grid-template-columns: repeat(8, 1fr);
        height: 100%;
        left: 50%;
        max-width: 1920px;
        opacity: 0;
        padding-bottom: var(--sectionSpacing);
        padding-top: calc(var(--navHeight) + var(--sectionSpacing));
        pointer-events: all;
        position: absolute;
        transform: translateX(-50%) translateY(var(--space-30));
        transition:
          opacity 0.2s ease,
          transform 0.5s ease 0.5s;
        top: 0;
        z-index: -1;
        width: calc(100% - var(--space-20));

        &.is-visible {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
          transition:
            opacity 0.2s ease 0.1s,
            transform 0.5s ease 0.1s;
        }

        @media (min-width: vars.$breakpoint-width-small) {
          width: calc(100% - var(--space-80));
          grid-template-columns: repeat(12, 1fr);
        }

        &.layout-1 .background-image {
          &:nth-child(1) {
            justify-self: flex-start;
            grid-column: 6 / span 3;

            @media (min-width: vars.$breakpoint-width-small) {
              align-self: flex-end;
              grid-column: 2 / span 3;
            }
          }

          &:nth-child(2) {
            grid-column: 1 / span 5;
            justify-self: flex-end;
            grid-row: 1;

            @media (min-width: vars.$breakpoint-width-small) {
              grid-column: 9 / span 3;
            }
          }
        }

        &.layout-2 .background-image {
          &:nth-child(1) {
            grid-column: 4 / span 5;
            justify-self: flex-start;
            @media (min-width: vars.$breakpoint-width-small) {
              align-self: flex-end;
              grid-column: 2 / span 4;
            }
          }

          &:nth-child(2) {
            grid-column: 1 / span 3;
            grid-row: 1;
            justify-self: flex-end;
            @media (min-width: vars.$breakpoint-width-small) {
              grid-column: 10 / span 2;
            }
          }
        }
      }

      &-image {
        :global {
          img {
            border-radius: 12px;
            margin-top: var(--space-10);
            max-width: 100%;
            object-fit: cover;

            @media (min-width: vars.$breakpoint-width-small) {
              margin-bottom: var(--space-10);
              max-height: clamp(150px, 28vmin, 400px);
              max-width: clamp(240px, 30vw, 440px);
            }

            @media (min-width: vars.$breakpoint-width-extra-large) {
              max-height: clamp(150px, 40vmin, 600px);
              max-width: clamp(240px, 30vw, 640px);
            }
          }

          p {
            font-family: var(--font-family-body);
            font-size: var(--font-size-12);
            line-height: 1.16667;

            strong {
              font-weight: var(--font-weight-semibold);
            }

            @media (min-width: vars.$breakpoint-width-small) {
              font-size: var(--font-size-body);
            }
          }

          .BoxComponent {
            display: flex;
            flex-direction: column-reverse;

            @media (min-width: vars.$breakpoint-width-small) {
              flex-direction: column;
            }
          }
        }

        &:nth-child(1) {
          order: 1;
          margin-bottom: clamp(var(--space-60), 3vw, var(--space-80));
          text-align: right;

          @media (min-width: vars.$breakpoint-width-small) {
            align-self: flex-end;
            order: 0;
            text-align: left;
          }
        }

        &:nth-child(2) {
          grid-column: 1 / span 4;
          margin-top: var(--space-80);
          text-align: left;

          @media (min-width: vars.$breakpoint-width-small) {
            grid-column: 9 / span 4;
            margin-top: 0;
            order: 1;
            text-align: right;
          }
        }
      }
    }

    .threlte {
      height: calc((var(--windowHeight) * var(--sections)) + var(--windowHeight) / 2);
      position: relative;

      &-container {
        height: 100vh;
        position: sticky;
        top: 0;
        z-index: 0;
      }

      &-scene {
        position: absolute;
        height: 100vh;
        width: 100vw;

        &-loader {
          --iconSize: 48px;
          animation: spin 1.5s infinite linear;
          background-image: url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='Radial spinner'%3E%3Cpath id='Vector' fill-rule='evenodd' clip-rule='evenodd' d='M19 12C19 8.13401 15.866 5 12 5V3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12H5C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12Z' fill='%237f7d7a'/%3E%3C/g%3E%3C/svg%3E%0A");
          height: var(--iconSize);
          left: 50%;
          pointer-events: none;
          position: absolute;
          top: 50%;
          transform: translate(-50%, -50%);
          transition: opacity var(--duration-default) var(--easing-default);
          width: var(--iconSize);
          opacity: 0;
          visibility: hidden;

          &.isLoading {
            opacity: 1;
            visibility: visible;
            pointer-events: all;
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        }
      }

      canvas {
        position: absolute;
        pointer-events: none;
      }

      @media (min-width: 1024px) {
        // 80vh accounts for 20vh margin bottom from hero,
        height: calc((var(--windowHeight) * var(--sections)) + var(--windowHeight) + var(--navHeight));
      }
    }

    .threlte.is-small {
      height: auto !important;

      .threlte-container {
        height: auto;
      }
      .copy-section {
        height: auto;
        left: unset;
        margin: 0 auto;
        transform: none;
        padding: var(--space-80) clamp(16px, 5vw, 40px);
        position: relative;
        top: unset;
        width: 100%;

        &-container {
          position: static;
        }

        &-images {
          display: flex;
          gap: var(--space-20);
          grid-column: 1 / -1;
          margin: 0 auto;
          width: 100%;
        }

        .copy {
          grid-column: 1 / -1;
          position: static;
        }

        .foreground-image {
          bottom: 5%;
          left: 50%;
          max-height: 30%;
          max-width: 55%;
          position: absolute;
          transform: translateX(-50%);

          :global(img) {
            max-height: min(330px, 60vw);
          }
        }
      }

      .hardware-sections {
        height: auto;
        position: static;
      }
    }

    :global {
      .background-image {
        // opacity: 0;
        &:nth-child(2) {
          & > .BoxComponent {
            display: flex;
            flex-direction: column-reverse;

            @media screen and (min-width: vars.$breakpoint-width-small) {
              display: block;
            }
          }
        }
      }

      // PWDS Monochrome test
      .BasePage[data-design-system='Public Web Design System'] .HomepageHardwareScroll.sq-custom-pwds-monochrome {
        .Action {
          &__icon {
            svg {
              height: 28px;
              opacity: 0;
              width: 28px;
            }
            &::before {
              background: black;
              border-radius: 50%;
              color: white;
              content: '→';
              height: 28px;
              line-height: 26px;
              position: absolute;
              text-align: center;
              top: 0;
              transition: transform var(--duration-quick) var(--easing-decelerate);
              width: 28px;
              z-index: -1;
            }
          }

          &:hover .Action__icon::before {
            transform: translateX(30%);
          }
        }
      }
    }

    @include mediaQueries.until('medium') {
      @media (min-aspect-ratio: 8/5) {
        .threlte-scene {
          transform: scale(0.8);
          transform-origin: center bottom;
        }

        .hardware-section-headline {
          max-width: 17rem;
          :global {
            h2 {
              font-size: 20px;
              line-height: 25px;
            }
          }
        }
        .background-images {
          --sectionSpacing: 0px;
          grid-template-columns: repeat(14, 1fr);
          grid-gap: var(--space-10);

          .background-image {
            opacity: 0;
            width: 100%;
          }

          :global {
            p {
              margin-top: 0;
            }
            img {
              margin: 0;
            }
          }
          &.layout-1 .background-image:nth-child(1),
          &.layout-2 .background-image:nth-child(2) {
            grid-column: 10 / span 5;
            grid-row: 1;
            :global {
              img {
                aspect-ratio: 1;
                object-position: top;
              }
              .BoxComponent {
                display: flex;
                flex-direction: row-reverse;
                align-items: flex-end;
                gap: 8px;
                .AproposImageComponent {
                  flex-basis: 60%;
                  max-width: 30vh;
                }
                .TextUnitComponent {
                  flex-basis: 40%;
                  text-align: right;
                }
              }
            }
          }

          &.layout-1 .background-image:nth-child(2),
          &.layout-2 .background-image:nth-child(1) {
            grid-column: 9 / span 6;
            grid-row: 2;

            :global {
              .BoxComponent {
                display: flex;
                flex-direction: row-reverse;
                align-items: flex-end;
                text-align: right;
                gap: 8px;
              }
            }
          }
          &.layout-1 .background-image {
            margin: 0;

            &:nth-child(1) {
              .BoxComponent {
                position: relative;

                @media (max-width: vars.$breakpoint-width-small) {
                  .TextUnitComponent {
                    position: absolute;
                    top: 0;
                    right: calc(100% + var(--space-10));
                    width: 100%;
                  }

                  img {
                    margin-top: 0;
                  }
                }
              }
            }
          }

          &.layout-2 .background-image {
            margin: 0;
            &:nth-child(1) {
              grid-row: 1;
              :global {
                .BoxComponent {
                  align-items: flex-start;
                }
              }
            }

            &:nth-child(2) {
              grid-row: 2;
            }
          }
        }
      }

      @media (min-aspect-ratio: 12/5) {
        .background-images {
          &.layout-1 .background-image {
            &:nth-child(2) {
              grid-column: 9 / span 6;
            }
          }
          &.layout-2 .background-image {
            &:nth-child(1) {
              :global {
                .BoxComponent {
                  align-items: flex-start;
                }
              }
            }
          }
        }
      }
    }

    @include mediaQueries.until('large') {
      @media (min-aspect-ratio: 4/3) and (orientation: landscape) {
        .hardware-section-headline {
          max-width: 35rem;
          :global {
            h2 {
              font-size: 30px;
              line-height: 35px;
            }
          }
        }
      }
    }

    @media (min-aspect-ratio: 2/1) {
      @include mediaQueries.until('large') {
        @include mediaQueries.at('medium') {
          .background-image:first-child {
            display: none;
          }

          .hardware-section-disclaimer {
            :global(.InlineTypographyComponent) {
              font-size: 10px;
              line-height: var(--font-size-12);
            }
          }
        }
      }

      @include mediaQueries.at('large') {
        .background-image:last-child {
          :global {
            .TextUnitComponent > .gridWrapper {
              justify-content: flex-end;
            }
          }
        }
      }
    }
  }
</style>
